import { useRef, useEffect, useState, useCallback } from 'react'
import { Loader2 } from 'lucide-react'
import VideoControls from './VideoControls'
import useVideo from './useVideo'
import useCanvas from './useCanvas'
import { cn } from '@/lib/utils'
import _ from 'lodash'
import ResponsiveImage from '@/components/mediaBuying/ResponsiveImage'

const Media = ({
    type,
    url,
    videoRef,
    handleMediaLoad,
    handleVideoLoad,
    handlePlay,
    handlePause,
    handleDurationChange,
    autoStart,
    muted,
}) => {
    if (type === 'image') {
        return <img ref={videoRef} src={url} className="tw:hidden" onLoad={handleMediaLoad} />
    }

    return (
        <video
            loop
            muted={muted}
            autoPlay={autoStart}
            playsInline
            preload="auto"
            src={url}
            ref={videoRef}
            className="tw:absolute tw:top-0 tw:left-0 tw:w-20 tw:h-20 tw:opacity-0 tw:z-10"
            crossOrigin="anonymous"
            onLoadedData={handleVideoLoad}
            onPlay={handlePlay}
            onPause={handlePause}
            onLoadedMetadata={handleVideoLoad}
            onDurationChange={handleDurationChange}
        />
    )
}

const CroppedMediaPreview = ({
    type,
    url,
    thumbnails,
    cropData,
    aspect,
    width,
    height,
    autoStart = true,
    minWidth,
    minHeight,
    startTime,
    endTime,
    startMuted = true,
}) => {
    const videoRef = useRef(null)
    const canvasRef = useRef(null)
    const animationFrameRef = useRef(null)
    const hideControlsTimeout = useRef(null)
    const hasDrawnFirstFrame = useRef(false)

    const [videoLoaded, setVideoLoaded] = useState(false)
    const [showControls, setShowControls] = useState(false)
    const [showPlayButton, setShowPlayButton] = useState(true)
    const [isHovering, setIsHovering] = useState(false)
    const [showingThumbnail, setShowingThumbnail] = useState(
        type === 'video' && !_.isEmpty(thumbnails) && thumbnails.length > 0 && !autoStart,
    )

    const {
        isPlaying,
        currentTime,
        duration,
        volume,
        isMuted,
        isSeekComplete,
        handlePlayPause: originalHandlePlayPause,
        handlePlay,
        handlePause,
        handleTimeUpdate,
        handleDurationChange,
        handleSeek,
        handleVolumeChange,
        handleMuteToggle,
        setIsMuted,
    } = useVideo(videoRef, type, startTime, endTime, startMuted)

    // Browsers block unmuted autoplay until the user has interacted with the page; fall back to muted playback.
    const handleAutoplayBlocked = useCallback(() => {
        const video = videoRef.current
        if (!video || video.muted) return

        video.muted = true
        setIsMuted(true)
        video.play().catch(() => {})
    }, [setIsMuted])

    const handlePlayPause = useCallback(() => {
        if (showingThumbnail) setShowingThumbnail(false)
        originalHandlePlayPause()
    }, [showingThumbnail, originalHandlePlayPause])

    const { drawFrame, handleMediaLoad, getCanvasDimensions } = useCanvas({
        videoRef,
        canvasRef,
        cropData,
        aspect,
        width,
        height,
        minWidth,
        minHeight,
        videoLoaded,
        setVideoLoaded,
        isPlaying,
        type,
        autoStart,
        isSeekComplete,
        onAutoplayBlocked: handleAutoplayBlocked,
    })

    // Ensure first frame is displayed when autoplay is off - wait for seek completion if needed
    useEffect(() => {
        if (!autoStart && type === 'video' && videoRef.current && !hasDrawnFirstFrame.current && isSeekComplete) {
            const video = videoRef.current

            const handleFirstFrame = () => {
                if (video.readyState >= 2) {
                    drawFrame()
                    hasDrawnFirstFrame.current = true
                }
            }

            // If video already has data and seek is complete, draw immediately
            if (video.readyState >= 2) {
                drawFrame()
                hasDrawnFirstFrame.current = true
            } else {
                video.addEventListener('loadeddata', handleFirstFrame, { once: true })
                return () => {
                    video.removeEventListener('loadeddata', handleFirstFrame)
                }
            }
        }
    }, [autoStart, type, drawFrame, isSeekComplete])

    // Auto-draw when playing
    useEffect(() => {
        if (isPlaying && videoLoaded) drawFrame()
        else if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)

        return () => {
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
        }
    }, [isPlaying, videoLoaded, drawFrame])

    // Redraw on crop/aspect change - wait for seek completion if needed
    useEffect(() => {
        if (videoLoaded && canvasRef.current && isSeekComplete) {
            const { width: canvasWidth, height: canvasHeight } = getCanvasDimensions()
            canvasRef.current.width = canvasWidth
            canvasRef.current.height = canvasHeight
            drawFrame()
        }
    }, [cropData, aspect, videoLoaded, drawFrame, getCanvasDimensions, isSeekComplete])

    // Force immediate redraw when cropData changes for images
    useEffect(() => {
        if (type === 'image' && canvasRef.current && videoRef.current) {
            // For images, ensure crop shows immediately when cropData changes
            setTimeout(() => {
                drawFrame()
            }, 10)
        }
    }, [cropData, type, drawFrame])

    // Try to load image if already complete
    useEffect(() => {
        const media = videoRef.current
        if (media && media.tagName === 'IMG' && media.complete && !videoLoaded) {
            handleMediaLoad()
        }
    }, [handleMediaLoad, videoLoaded])

    // Ensure first frame drawn when paused - wait for seek completion if needed
    useEffect(() => {
        const media = videoRef.current
        if (media && type === 'video' && videoLoaded && !isPlaying && isSeekComplete) {
            const ensureFrame = () => {
                if (canvasRef.current && media.readyState >= 2) drawFrame()
            }
            ensureFrame()
            const timer = setTimeout(ensureFrame, 200)
            return () => clearTimeout(timer)
        }
    }, [videoLoaded, isPlaying, type, drawFrame, isSeekComplete])

    // Show play button when paused
    useEffect(() => {
        if (type === 'video' && videoLoaded && !isPlaying) {
            setShowPlayButton(true)
        }
    }, [type, videoLoaded, isPlaying])

    const handleMouseEnter = useCallback(() => {
        if (type !== 'video') return
        setIsHovering(true)
        setShowControls(true)
        if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current)
    }, [type])

    const handleMouseLeave = useCallback(() => {
        if (type !== 'video') return
        setIsHovering(false)
        if (isPlaying) {
            hideControlsTimeout.current = setTimeout(() => {
                setShowControls(false)
            }, 1000)
        }
    }, [type, isPlaying])

    const handleMouseMove = useCallback(() => {
        if (type !== 'video' || !isHovering) return
        setShowControls(true)
        if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current)
        // Don't hide controls while hovering
    }, [type, isHovering])

    useEffect(() => {
        if (type === 'video') {
            setShowControls(true)
        }

        return () => {
            if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
            if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current)
        }
    }, [type])

    // Auto-hide controls when playing starts (but not if hovering)
    useEffect(() => {
        if (type === 'video' && isPlaying && !isHovering) {
            hideControlsTimeout.current = setTimeout(() => {
                setShowControls(false)
            }, 1000)
        }
        return () => {
            if (hideControlsTimeout.current) clearTimeout(hideControlsTimeout.current)
        }
    }, [type, isPlaying, isHovering])

    const forceDrawFrame = () => drawFrame()

    const { width: canvasWidth, height: canvasHeight } = getCanvasDimensions()

    return (
        <div
            className="tw:relative tw:w-full tw:h-full tw:flex tw:items-center tw:justify-center"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
        >
            {/* Thumbnail - shown initially for videos with thumbnailUrl */}
            {showingThumbnail && (
                <ResponsiveImage
                    images={thumbnails}
                    alt="Video thumbnail"
                    onClick={handlePlayPause}
                    className="tw:block tw:h-full tw:w-full tw:max-w-full tw:max-h-full tw:cursor-pointer tw:object-cover"
                />
            )}

            {/* Video/canvas - hidden when showing thumbnail */}
            <div className={cn('tw:w-full tw:h-full tw:block', { 'tw:hidden': showingThumbnail })}>
                <Media
                    type={type}
                    url={url}
                    videoRef={videoRef}
                    muted={isMuted}
                    autoStart={autoStart}
                    handlePlay={handlePlay}
                    handlePause={handlePause}
                    handleMediaLoad={handleMediaLoad}
                    handleVideoLoad={handleMediaLoad}
                    handleTimeUpdate={handleTimeUpdate}
                    handleDurationChange={handleDurationChange}
                />

                <canvas
                    ref={canvasRef}
                    width={canvasWidth}
                    height={canvasHeight}
                    onClick={type === 'video' ? handlePlayPause : forceDrawFrame}
                    className="tw:block tw:max-w-full tw:max-h-full tw:bg-gray-300"
                    title={type === 'video' ? (isPlaying ? 'Click to pause' : 'Click to play') : 'Click to redraw'}
                    style={{
                        width: '100%',
                        height: `${Math.max(canvasHeight, minHeight)}px`,
                        objectFit: 'cover',
                    }}
                />
            </div>

            {type === 'video' && videoLoaded && showControls && (
                <VideoControls
                    showControls={showControls}
                    handleSeek={handleSeek}
                    duration={duration}
                    isMuted={isMuted}
                    handleVolumeChange={handleVolumeChange}
                    volume={volume}
                    handleMuteToggle={handleMuteToggle}
                    currentTime={currentTime}
                    handlePlayPause={handlePlayPause}
                    showPlayButton={showPlayButton}
                    isPlaying={isPlaying}
                />
            )}

            {!videoLoaded && (
                <div className="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center">
                    <Loader2 className="tw:h-3 tw:w-3 tw:animate-spin" />
                </div>
            )}
        </div>
    )
}

export default CroppedMediaPreview
